// M3 tankers, routes, Suez, embargoes (M3_SPEC §9.3-§9.4). From 1946, every field in a region with a loading
// terminal (rules.TERMINALS) ships its crude by route freight instead of the M2 flat ocean freight (§18.10):
//   nm      = route.direct ?? (suezOpen && ship.suezable ? route.suez : route.cape)
//   freight = FREIGHT_BASE_1970[ship] × CPI(1970→y) × nm/1000 × freightIndex(y) × (Jones Act ? 2.5 : 1)
//           + war-risk premium (spot × warRisk × insuranceMult on the tanker-war / Gulf-crisis routes)
// The tanker charter's own flat costPerBbl is then dropped (m2/index transportMult); pipelines keep their tariffs.
// Alaska: Valdez → West Coast is the Jones Act leg; TAPS is the pipeline tariff (no double count, §18.10).
import {
  TERMINALS, FREIGHT_BASE_1970, FREIGHT_BASE_YEAR, freightIndex, JONES_ACT_MULT, suezOpen, SUEZ_CLOSURES, ROUTE_INTERRUPTIONS,
  M3_FROM, spillRegime,
} from '../../data/m3/rules.js';
import { inflate } from '../../data/cpi.js';
import { eqDef, fieldDef, regionDef, fieldTransports, yf, stat } from '../defs.js';
import { spotPrice } from '../../economy/market.js';
import { econState } from './econ.js';

export const MARKETS = ['europe', 'us_gulf', 'us_west', 'japan'];
export const MARKET_NAMES = { europe: 'Rotterdam (Europe)', us_gulf: 'US Gulf Coast', us_west: 'US West Coast', japan: 'Japan' };
// embargo destinations → our market ids ('netherlands'/'uk' are European ports: Europe stays open via other ports)
const DEST_MARKETS = { us: ['us_gulf', 'us_west'] };
// UN sanctions close a region's exports at every terminal
export const SANCTIONS = [{ routeId: 'iraq_sanctions', regionIds: ['iraq'] }];
const SHIP_CLASSES = ['tanker', 'supertanker_charter', 'vlcc_charter', 'ulcc_charter', 'double_hull_charter', 'coastal_tanker'];
const SUEZABLE = { tanker: true, coastal_tanker: true, supertanker_charter: true, vlcc_charter: false, ulcc_charter: false, double_hull_charter: false };
const PIPE_RE = /pipe|tapline|taps/;
export const BBL_PER_DWT = 7.3;

export const terminalsForRegion = (rid) => Object.entries(TERMINALS).filter(([, t]) => t.regionIds.includes(rid)).map(([id, t]) => ({ id, ...t }));
const HAS_TERMINAL = new Map(); // static data
export const hasTerminal = (rid) => { let v = HAS_TERMINAL.get(rid); if (v === undefined) HAS_TERMINAL.set(rid, (v = Object.values(TERMINALS).some((t) => t.regionIds.includes(rid)))); return v; };

// §18.11: tanker transports `requires: 'port'`. A field has a port if its region has a loading terminal, it is offshore,
// or its own transport list carries tankers (coastal fields).
export function fieldHasPort(fdef) {
  if (!fdef) return false;
  if (hasTerminal(fdef.regionId)) return true;
  if ((fdef.waterDepthFt ?? 0) > 0 || (fdef.waterDepthMaxFt ?? 0) > 0) return true;
  if (['shelf', 'lake', 'swamp', 'harsh', 'deepwater'].includes(fdef.environment?.kind)) return true;
  return fieldTransports(fdef).some((t) => /tanker|charter/.test(t.id));
}

export function activeInterruptions(y) {
  return ROUTE_INTERRUPTIONS.filter((r) => y >= r.from && y < r.until);
}
export function regionSanctioned(rid, y) {
  return SANCTIONS.find((s) => s.regionIds.includes(rid) && activeInterruptions(y).some((r) => r.id === s.routeId)) ?? null;
}

// Embargoed markets for a region today (scripted from the news effect `embargo`, stored with its end day).
export function embargoedMarkets(state, rid) {
  const out = new Set();
  for (const e of state.m3econ?.embargoes ?? []) {
    if (state.day < e.fromDay || state.day >= e.untilDay || !e.regionIds.includes(rid)) continue;
    for (const m of e.markets) out.add(m);
  }
  return out;
}

export function addEmbargo(state, item, e) {
  const E = econState(state);
  const markets = [...new Set((e.destinations ?? []).flatMap((d) => DEST_MARKETS[d] ?? []))];
  const rec = { id: item?.id ?? `embargo_${state.day}`, regionIds: e.regionIds ?? [], destinations: e.destinations ?? [], markets, fromDay: state.day, untilDay: state.day + (e.days ?? 90) };
  if (E.embargoes.some((x) => x.id === rec.id)) return null;
  E.embargoes.push(rec);
  E.embargoes = E.embargoes.filter((x) => x.untilDay > state.day - 3650);
  return rec;
}

// Ship class carrying the field's crude: its tanker transport, else the cheapest tanker its transport list offers now.
export function shipClass(state, fieldId) {
  const f = state.fields[fieldId];
  if (f?.transportId && FREIGHT_BASE_1970[f.transportId] != null) return f.transportId;
  const y = yf(state);
  const listed = fieldTransports(fieldDef(fieldId)).filter((t) => FREIGHT_BASE_1970[t.id] != null && t.id !== 'coastal_tanker' && (t.year ?? 0) <= y && (eqDef(t.id)?.year ?? 0) <= y);
  if (!listed.length) return 'tanker';
  listed.sort((a, b) => FREIGHT_BASE_1970[a.id] - FREIGHT_BASE_1970[b.id]);
  return listed[0].id;
}

function terminalUsable(state, fieldId, term, y) {
  if (term.from != null && y < term.from) return 'not built yet';
  const tid = state.fields[fieldId]?.transportId ?? '';
  if (term.via === 'tapline' && tid !== 'tapline') return 'reached only through Tapline';
  if (term.via === 'pipeline' && !PIPE_RE.test(tid)) return 'reached only by pipeline';
  for (const r of activeInterruptions(y)) {
    if (r.terminal === term.id && !r.warRisk) return r.reason;
    if (r.transport && term.via === r.transport) return r.reason;
  }
  return null;
}

// Freight per bbl (era $) on one route.
export function routeFreight(state, termId, market, cls, y = yf(state)) {
  const term = TERMINALS[termId];
  const r = term?.routes?.[market];
  if (!r) return null;
  const suezable = SUEZABLE[cls] ?? true;
  const viaSuez = r.direct == null && suezOpen(y) && suezable && r.suez != null;
  const nm = r.direct ?? (viaSuez ? r.suez : r.cape ?? r.suez);
  const via = r.direct != null ? 'direct' : viaSuez ? 'suez' : 'cape';
  const base = FREIGHT_BASE_1970[cls] ?? FREIGHT_BASE_1970.tanker;
  let freight = base * inflate(1, FREIGHT_BASE_YEAR, Math.min(y, 2025)) * (nm / 1000) * freightIndex(y) * (term.jonesAct ? JONES_ACT_MULT : 1);
  let warRisk = 0;
  for (const ir of activeInterruptions(y)) if (ir.terminal === termId && ir.warRisk) warRisk = Math.max(warRisk, ir.warRisk * (ir.insuranceMult ?? 1));
  const war = warRisk * Math.max(0, spotPrice(state));
  return { terminal: termId, market, nm, via, cls, freight: freight + war, base: freight, warPremium: war, suezClosed: !suezOpen(y) };
}

// All routes for a field with availability, sorted cheapest first.
export function routeOptions(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const rid = fdef?.regionId;
  const y = yf(state);
  if (!rid || y < M3_FROM || !hasTerminal(rid)) return [];
  const cls = shipClass(state, fieldId);
  const emb = embargoedMarkets(state, rid);
  const sanc = regionSanctioned(rid, y);
  const out = [];
  for (const term of terminalsForRegion(rid)) {
    const why = terminalUsable(state, fieldId, term, y);
    for (const market of Object.keys(term.routes)) {
      const rf = routeFreight(state, term.id, market, cls, y);
      if (!rf) continue;
      const blocked = sanc ? 'UN sanctions on the region’s oil' : why ?? (emb.has(market) ? 'Arab oil embargo' : null);
      out.push({ ...rf, terminalName: term.id.replace(/_/g, ' '), marketName: MARKET_NAMES[market] ?? market, blocked });
    }
  }
  // pipeline-fed terminals (Sidon, Banias, Ceyhan) win ties: the crude is already there
  out.sort((a, b) => (a.blocked ? 1 : 0) - (b.blocked ? 1 : 0) || a.freight - b.freight);
  return out;
}

// The route in use: the player's choice when open, else the cheapest open one. null = region has no terminal.
export function routeFor(state, fieldId) {
  const opts = routeOptions(state, fieldId);
  if (!opts.length) return null;
  const pref = state.m3econ?.routes?.[fieldId];
  const chosen = pref ? opts.find((o) => o.terminal === pref.terminal && o.market === pref.market && !o.blocked) : null;
  const open = opts.filter((o) => !o.blocked);
  const r = chosen ?? open[0] ?? null;
  if (!r) return { blocked: opts[0].blocked, options: opts, freight: 0 };
  return { ...r, chosen: !!chosen, options: opts };
}

// production.regionShipping hook: route freight per bbl, or null to keep the M2 rule.
export function m3RegionShipping(state, fieldId) {
  if (yf(state) < M3_FROM) return null;
  const r = routeFor(state, fieldId);
  if (!r) return null;
  return r.blocked ? 0 : r.freight;
}

// production.shipAndSell: reason when the field's crude cannot be sold anywhere today.
export function saleBlocked(state, fieldId) {
  if (yf(state) < M3_FROM) return null;
  const r = routeFor(state, fieldId);
  return r?.blocked ?? null;
}

// m2/index.transportMult: tanker charters on route-priced fields carry no flat rate (route freight replaces it).
export function routePricedTransport(state, fieldId, tdef) {
  if (!tdef || yf(state) < M3_FROM) return false;
  if (!SHIP_CLASSES.includes(tdef.id) && !stat(tdef, 'routeBased', false)) return false;
  return hasTerminal(fieldDef(fieldId)?.regionId);
}

export function chooseRouteImpl(state, fieldId, choice) {
  if (!state.fields[fieldId]) return { ok: false, msg: 'No such field.' };
  const E = econState(state);
  if (!choice) { delete E.routes[fieldId]; return { ok: true, msg: 'Cargoes go wherever freight is cheapest.' }; }
  const opts = routeOptions(state, fieldId);
  const o = opts.find((x) => x.terminal === choice.terminal && x.market === choice.market);
  if (!o) return { ok: false, msg: 'No such route from this field.' };
  if (o.blocked) return { ok: false, msg: `That route is closed: ${o.blocked}.` };
  E.routes[fieldId] = { terminal: o.terminal, market: o.market };
  return { ok: true, msg: `${fieldDef(fieldId).name} crude now loads at ${o.terminalName} for ${o.marketName} (${Math.round(o.nm).toLocaleString('en-US')} nm${o.via === 'cape' ? ' around the Cape' : o.via === 'suez' ? ' via Suez' : ''}).` };
}

// Tanker casualties: per voyage (cargo / ship size) risk 0.0002 × spillRiskMult (M3_SPEC §13). Returns spilled bbl.
export const TANKER_LOSS_PER_VOYAGE = 0.0002;
export function tankerLossRoll(state, fieldId, bbl, rollFn) {
  const r = routeFor(state, fieldId);
  if (!r || r.blocked || !(bbl > 0)) return 0;
  const def = eqDef(r.cls);
  const cargo = (stat(def, 'dwt', 30000)) * BBL_PER_DWT;
  const voyages = bbl / cargo;
  const p = TANKER_LOSS_PER_VOYAGE * stat(def, 'spillRiskMult', 1) * voyages;
  return rollFn(p) ? Math.min(bbl * 3, cargo) * 0.3 : 0;
}

export function routeView(state, fieldId) {
  const fdef = fieldDef(fieldId);
  if (!fdef) return null;
  const y = yf(state);
  const r = routeFor(state, fieldId);
  return {
    fieldId, regionId: fdef.regionId, region: regionDef(fdef.regionId)?.name ?? fdef.regionId,
    routed: !!r, ship: r ? shipClass(state, fieldId) : null,
    current: r && !r.blocked ? { terminal: r.terminal, market: r.market, nm: r.nm, via: r.via, freight: r.freight, warPremium: r.warPremium, chosen: r.chosen } : null,
    blocked: r?.blocked ?? null, options: r?.options ?? [], suezOpen: suezOpen(y),
    suezClosures: SUEZ_CLOSURES.filter((c) => y >= c.from).map((c) => ({ ...c, open: y >= c.until })),
    interruptions: activeInterruptions(y).filter((i) => terminalsForRegion(fdef.regionId).some((t) => t.id === i.terminal || t.via === i.transport)).map((i) => ({ ...i })),
    embargo: [...embargoedMarkets(state, fdef.regionId)], hasPort: fieldHasPort(fdef), spillRegime: spillRegime(y).label,
  };
}
